import React, { useEffect, useState } from 'react';
import { ChevronDownIcon, ArrowUpIcon, ArrowDownIcon, InfoIcon } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, Legend } from 'recharts';
export function Dashboard() {
  const [timeFrame, setTimeFrame] = useState<'today' | 'month' | 'year'>('today');
  const [selectedBank, setSelectedBank] = useState<string>('all');
  const [showTimeFrameDropdown, setShowTimeFrameDropdown] = useState(false);
  const [showBankDropdown, setShowBankDropdown] = useState(false);
  const [filteredKpiData, setFilteredKpiData] = useState<any[]>([]);
  const [kpiFilters, setKpiFilters] = useState<{
    [key: string]: 'all' | 'success' | 'failed';
  }>({
    'P2P Transfers': 'all',
    'QRC Payments': 'all',
    'RTP Payments': 'all',
    'Bulk Payments': 'all',
    'Bulk Receiving': 'all',
    'ALIAS Registered': 'all'
  });
  const banks = [{
    code: 'all',
    name: 'All Banks'
  }, {
    code: 'AMHRETAA',
    name: 'Amhara Bank S.C.'
  }, {
    code: 'AWINETAA',
    name: 'Awash Bank S.C.'
  }, {
    code: 'CBETETAA',
    name: 'Commercial Bank of Ethiopia (CBE)'
  }, {
    code: 'BUNAETAA',
    name: 'Bunna International Bank S.C.'
  }, {
    code: 'DASHETAA',
    name: 'Dashen Bank S.C.'
  }, {
    code: 'CBORETAA',
    name: 'Cooperative Bank of Oromia S.C. (COOP)'
  }, {
    code: 'ZEMEETAA',
    name: 'Zemen Bank S.C.'
  }, {
    code: 'NIBIETAA',
    name: 'NIB International Bank S.C. (NIB)'
  }, {
    code: 'HIJRETAA',
    name: 'Hibret Bank S.C.'
  }, {
    code: 'TSCPETAA',
    name: 'Tsehay Bank S.C.'
  }, {
    code: 'WEGAETAA',
    name: 'Wegagen Bank S.C.'
  }, {
    code: 'ABAYETAA',
    name: 'Abay Bank S.C.'
  }];
  // Base KPI data
  const getKpiData = () => {
    // Generate data based on selectedBank and timeFrame
    const multiplier = timeFrame === 'today' ? 1 : timeFrame === 'month' ? 30 : 365;
    const bankFactor = selectedBank === 'all' ? 1 : 0.7; // Reduce by 30% for specific bank
    return [{
      title: 'P2P Transfers',
      count: {
        all: Math.round(1243 * multiplier * bankFactor),
        success: Math.round(1105 * multiplier * bankFactor),
        failed: Math.round(138 * multiplier * bankFactor)
      },
      value: {
        all: Math.round(4528900 * multiplier * bankFactor),
        success: Math.round(4025000 * multiplier * bankFactor),
        failed: Math.round(503900 * multiplier * bankFactor)
      },
      trend: 'up',
      percent: 12
    }, {
      title: 'QRC Payments',
      count: {
        all: Math.round(892 * multiplier * bankFactor),
        success: Math.round(845 * multiplier * bankFactor),
        failed: Math.round(47 * multiplier * bankFactor)
      },
      value: {
        all: Math.round(2134500 * multiplier * bankFactor),
        success: Math.round(2020300 * multiplier * bankFactor),
        failed: Math.round(114200 * multiplier * bankFactor)
      },
      trend: 'up',
      percent: 8
    }, {
      title: 'RTP Payments',
      count: {
        all: Math.round(567 * multiplier * bankFactor),
        success: Math.round(520 * multiplier * bankFactor),
        failed: Math.round(47 * multiplier * bankFactor)
      },
      value: {
        all: Math.round(1876300 * multiplier * bankFactor),
        success: Math.round(1750000 * multiplier * bankFactor),
        failed: Math.round(126300 * multiplier * bankFactor)
      },
      trend: 'down',
      percent: 3
    }, {
      title: 'Bulk Payments',
      count: {
        all: Math.round(124 * multiplier * bankFactor),
        success: Math.round(118 * multiplier * bankFactor),
        failed: Math.round(6 * multiplier * bankFactor)
      },
      value: {
        all: Math.round(5432100 * multiplier * bankFactor),
        success: Math.round(5300000 * multiplier * bankFactor),
        failed: Math.round(132100 * multiplier * bankFactor)
      },
      trend: 'up',
      percent: 15
    }, {
      title: 'Bulk Receiving',
      count: {
        all: Math.round(98 * multiplier * bankFactor),
        success: Math.round(92 * multiplier * bankFactor),
        failed: Math.round(6 * multiplier * bankFactor)
      },
      value: {
        all: Math.round(4321000 * multiplier * bankFactor),
        success: Math.round(4150000 * multiplier * bankFactor),
        failed: Math.round(171000 * multiplier * bankFactor)
      },
      trend: 'down',
      percent: 5
    }, {
      title: 'ALIAS Registered',
      count: {
        all: Math.round(432 * multiplier * bankFactor),
        success: Math.round(432 * multiplier * bankFactor),
        failed: 0
      },
      value: {
        all: 0,
        success: 0,
        failed: 0
      },
      trend: 'up',
      percent: 23
    }];
  };
  // Sample data for charts - today
  const transactionDataToday = [{
    name: '00:00',
    p2p: 400,
    qrc: 240,
    rtp: 180,
    bulk: 100
  }, {
    name: '04:00',
    p2p: 300,
    qrc: 139,
    rtp: 280,
    bulk: 150
  }, {
    name: '08:00',
    p2p: 200,
    qrc: 980,
    rtp: 290,
    bulk: 210
  }, {
    name: '12:00',
    p2p: 278,
    qrc: 390,
    rtp: 200,
    bulk: 240
  }, {
    name: '16:00',
    p2p: 189,
    qrc: 480,
    rtp: 281,
    bulk: 180
  }, {
    name: '20:00',
    p2p: 239,
    qrc: 380,
    rtp: 250,
    bulk: 120
  }];
  // Sample data for charts - month
  const transactionDataMonth = [{
    name: 'Week 1',
    p2p: 1200,
    qrc: 840,
    rtp: 680,
    bulk: 500
  }, {
    name: 'Week 2',
    p2p: 1500,
    qrc: 939,
    rtp: 880,
    bulk: 650
  }, {
    name: 'Week 3',
    p2p: 1300,
    qrc: 1080,
    rtp: 790,
    bulk: 710
  }, {
    name: 'Week 4',
    p2p: 1400,
    qrc: 1190,
    rtp: 900,
    bulk: 740
  }];
  // Sample data for charts - year
  const transactionDataYear = [{
    name: 'Jan',
    p2p: 4200,
    qrc: 3240,
    rtp: 2180,
    bulk: 1500
  }, {
    name: 'Feb',
    p2p: 3800,
    qrc: 2939,
    rtp: 2380,
    bulk: 1650
  }, {
    name: 'Mar',
    p2p: 4500,
    qrc: 3580,
    rtp: 2590,
    bulk: 1710
  }, {
    name: 'Apr',
    p2p: 5200,
    qrc: 3890,
    rtp: 3200,
    bulk: 1840
  }, {
    name: 'May',
    p2p: 4800,
    qrc: 4080,
    rtp: 3281,
    bulk: 1780
  }, {
    name: 'Jun',
    p2p: 5500,
    qrc: 4380,
    rtp: 3650,
    bulk: 1920
  }];
  // Sample data for value charts - today
  const valueDataToday = [{
    name: '00:00',
    p2p: 2400,
    qrc: 1240,
    rtp: 1180,
    bulk: 1100
  }, {
    name: '04:00',
    p2p: 1300,
    qrc: 1139,
    rtp: 1280,
    bulk: 1150
  }, {
    name: '08:00',
    p2p: 1200,
    qrc: 1980,
    rtp: 1290,
    bulk: 1210
  }, {
    name: '12:00',
    p2p: 1278,
    qrc: 1390,
    rtp: 1200,
    bulk: 1240
  }, {
    name: '16:00',
    p2p: 1189,
    qrc: 1480,
    rtp: 1281,
    bulk: 1180
  }, {
    name: '20:00',
    p2p: 1239,
    qrc: 1380,
    rtp: 1250,
    bulk: 1120
  }];
  // Sample data for value charts - month
  const valueDataMonth = [{
    name: 'Week 1',
    p2p: 12400,
    qrc: 8240,
    rtp: 6180,
    bulk: 5100
  }, {
    name: 'Week 2',
    p2p: 15300,
    qrc: 9139,
    rtp: 8280,
    bulk: 6150
  }, {
    name: 'Week 3',
    p2p: 13200,
    qrc: 10980,
    rtp: 7290,
    bulk: 7210
  }, {
    name: 'Week 4',
    p2p: 14278,
    qrc: 11390,
    rtp: 9200,
    bulk: 7240
  }];
  // Sample data for value charts - year
  const valueDataYear = [{
    name: 'Jan',
    p2p: 42400,
    qrc: 32240,
    rtp: 21180,
    bulk: 15100
  }, {
    name: 'Feb',
    p2p: 38300,
    qrc: 29139,
    rtp: 23280,
    bulk: 16150
  }, {
    name: 'Mar',
    p2p: 45200,
    qrc: 35980,
    rtp: 25290,
    bulk: 17210
  }, {
    name: 'Apr',
    p2p: 52278,
    qrc: 38390,
    rtp: 32200,
    bulk: 18240
  }, {
    name: 'May',
    p2p: 48189,
    qrc: 40480,
    rtp: 32810,
    bulk: 17180
  }, {
    name: 'Jun',
    p2p: 55239,
    qrc: 43380,
    rtp: 36250,
    bulk: 19120
  }];
  // Apply bank filter to chart data
  const applyBankFilter = data => {
    if (selectedBank === 'all') return data;
    // Reduce values by a certain percentage for specific bank
    const factor = 0.7; // 70% of original values
    return data.map(item => {
      const newItem = {
        ...item
      };
      Object.keys(newItem).forEach(key => {
        if (key !== 'name') {
          newItem[key] = Math.round(newItem[key] * factor);
        }
      });
      return newItem;
    });
  };
  // Sample data for recent activity
  const getRecentActivity = () => {
    const baseActivity = [{
      id: 1,
      dateTime: '2023-05-10 14:30:22',
      sender: 'AMHRETAA',
      receiver: 'CBETETAA',
      amount: 5000,
      status: 'Success'
    }, {
      id: 2,
      dateTime: '2023-05-10 14:28:15',
      sender: 'AWINETAA',
      receiver: 'DASHETAA',
      amount: 3500,
      status: 'Failed'
    }, {
      id: 3,
      dateTime: '2023-05-10 14:25:47',
      sender: 'BUNAETAA',
      receiver: 'ZEMEETAA',
      amount: 2800,
      status: 'Success'
    }, {
      id: 4,
      dateTime: '2023-05-10 14:20:33',
      sender: 'NIBIETAA',
      receiver: 'HIJRETAA',
      amount: 4200,
      status: 'Success'
    }, {
      id: 5,
      dateTime: '2023-05-10 14:15:19',
      sender: 'TSCPETAA',
      receiver: 'WEGAETAA',
      amount: 1500,
      status: 'Failed'
    }];
    if (selectedBank === 'all') return baseActivity;
    // Filter to only show transactions involving the selected bank
    return baseActivity.filter(activity => activity.sender === selectedBank || activity.receiver === selectedBank);
  };
  // Get the appropriate transaction data based on timeframe
  const getTransactionData = () => {
    let data;
    switch (timeFrame) {
      case 'month':
        data = transactionDataMonth;
        break;
      case 'year':
        data = transactionDataYear;
        break;
      default:
        data = transactionDataToday;
    }
    return applyBankFilter(data);
  };
  // Get the appropriate value data based on timeframe
  const getValueData = () => {
    let data;
    switch (timeFrame) {
      case 'month':
        data = valueDataMonth;
        break;
      case 'year':
        data = valueDataYear;
        break;
      default:
        data = valueDataToday;
    }
    return applyBankFilter(data);
  };
  // Update KPI filters
  const handleKpiFilterChange = (title: string, filter: 'all' | 'success' | 'failed') => {
    setKpiFilters(prev => ({
      ...prev,
      [title]: filter
    }));
  };
  // Update filtered KPI data when filters change or when timeFrame/selectedBank changes
  useEffect(() => {
    const kpiData = getKpiData();
    const filtered = kpiData.map(kpi => {
      const filter = kpiFilters[kpi.title];
      return {
        ...kpi,
        displayCount: kpi.count[filter],
        displayValue: kpi.value[filter]
      };
    });
    setFilteredKpiData(filtered);
  }, [kpiFilters, timeFrame, selectedBank]);
  // Handle outside clicks for dropdowns
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.time-dropdown')) {
        setShowTimeFrameDropdown(false);
      }
      if (!target.closest('.bank-dropdown')) {
        setShowBankDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  const recentActivity = getRecentActivity();
  return <div className="space-y-6" data-id="element-330">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4" data-id="element-331">
        <h1 className="text-2xl font-bold text-[#1A1F36]" data-id="element-332">Dashboard</h1>
        <div className="flex flex-col sm:flex-row gap-3" data-id="element-333">
          <div className="relative time-dropdown" data-id="element-334">
            <button onClick={() => setShowTimeFrameDropdown(!showTimeFrameDropdown)} className="flex items-center bg-white border border-gray-300 rounded-md px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50" data-id="element-335">
              {timeFrame === 'today' && 'Today'}
              {timeFrame === 'month' && 'This Month'}
              {timeFrame === 'year' && 'This Year'}
              <ChevronDownIcon size={16} className="ml-2" data-id="element-336" />
            </button>
            {showTimeFrameDropdown && <div className="absolute right-0 mt-1 w-40 bg-white rounded-md shadow-lg z-10 border border-gray-200" data-id="element-337">
                <button onClick={() => {
              setTimeFrame('today');
              setShowTimeFrameDropdown(false);
            }} className={`block w-full text-left px-4 py-2 text-sm ${timeFrame === 'today' ? 'bg-gray-100 text-[#F58220]' : 'text-gray-700 hover:bg-gray-50'}`} data-id="element-338">
                  Today
                </button>
                <button onClick={() => {
              setTimeFrame('month');
              setShowTimeFrameDropdown(false);
            }} className={`block w-full text-left px-4 py-2 text-sm ${timeFrame === 'month' ? 'bg-gray-100 text-[#F58220]' : 'text-gray-700 hover:bg-gray-50'}`} data-id="element-339">
                  This Month
                </button>
                <button onClick={() => {
              setTimeFrame('year');
              setShowTimeFrameDropdown(false);
            }} className={`block w-full text-left px-4 py-2 text-sm ${timeFrame === 'year' ? 'bg-gray-100 text-[#F58220]' : 'text-gray-700 hover:bg-gray-50'}`} data-id="element-340">
                  This Year
                </button>
              </div>}
          </div>
          <div className="relative bank-dropdown" data-id="element-341">
            <button onClick={() => setShowBankDropdown(!showBankDropdown)} className="flex items-center bg-white border border-gray-300 rounded-md px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50" data-id="element-342">
              {selectedBank === 'all' ? 'All Banks' : banks.find(b => b.code === selectedBank)?.name}
              <ChevronDownIcon size={16} className="ml-2" data-id="element-343" />
            </button>
            {showBankDropdown && <div className="absolute right-0 mt-1 w-64 bg-white rounded-md shadow-lg z-10 border border-gray-200 max-h-60 overflow-y-auto" data-id="element-344">
                {banks.map(bank => <button key={bank.code} onClick={() => {
              setSelectedBank(bank.code);
              setShowBankDropdown(false);
            }} className={`block w-full text-left px-4 py-2 text-sm ${selectedBank === bank.code ? 'bg-gray-100 text-[#F58220]' : 'text-gray-700 hover:bg-gray-50'}`} data-id="element-345">
                    {bank.name}
                  </button>)}
              </div>}
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-346">
        {filteredKpiData.map((kpi, index) => <div key={index} className="bg-white rounded-lg shadow-sm border border-gray-100 p-4" data-id="element-347">
            <div className="flex justify-between items-start" data-id="element-348">
              <h3 className="text-sm font-medium text-gray-500" data-id="element-349">{kpi.title}</h3>
              <div className={`flex items-center text-xs font-medium ${kpi.trend === 'up' ? 'text-green-600' : 'text-red-600'}`} data-id="element-350">
                {kpi.trend === 'up' ? <ArrowUpIcon size={12} data-id="element-351" /> : <ArrowDownIcon size={12} data-id="element-352" />}
                <span className="ml-1" data-id="element-353">{kpi.percent}%</span>
              </div>
            </div>
            <div className="mt-2" data-id="element-354">
              <div className="text-2xl font-bold text-[#1A1F36]" data-id="element-355">
                {kpi.displayCount.toLocaleString()}
              </div>
              {kpi.displayValue > 0 && <div className="text-sm text-gray-600 mt-1" data-id="element-356">
                  ETB {kpi.displayValue.toLocaleString()}
                </div>}
            </div>
            <div className="mt-3 flex justify-between text-xs" data-id="element-357">
              <button className={`${kpiFilters[kpi.title] === 'all' ? 'text-[#F58220] font-medium' : 'text-gray-500'}`} onClick={() => handleKpiFilterChange(kpi.title, 'all')} data-id="element-358">
                All
              </button>
              <button className={`${kpiFilters[kpi.title] === 'success' ? 'text-[#F58220] font-medium' : 'text-gray-500'}`} onClick={() => handleKpiFilterChange(kpi.title, 'success')} data-id="element-359">
                Success
              </button>
              <button className={`${kpiFilters[kpi.title] === 'failed' ? 'text-[#F58220] font-medium' : 'text-gray-500'}`} onClick={() => handleKpiFilterChange(kpi.title, 'failed')} data-id="element-360">
                Failed
              </button>
            </div>
          </div>)}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-361">
        <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-4" data-id="element-362">
          <div className="flex justify-between items-center mb-4" data-id="element-363">
            <h3 className="font-medium text-[#1A1F36]" data-id="element-364">Transaction Count</h3>
            <div className="flex items-center text-xs text-gray-500" data-id="element-365">
              <InfoIcon size={14} className="mr-1" data-id="element-366" />
              <span data-id="element-367">
                {timeFrame === 'today' && 'Today'}
                {timeFrame === 'month' && 'This Month'}
                {timeFrame === 'year' && 'This Year'}
              </span>
            </div>
          </div>
          <div className="h-64" data-id="element-368">
            <ResponsiveContainer width="100%" height="100%" data-id="element-369">
              <AreaChart data={getTransactionData()} margin={{
              top: 10,
              right: 30,
              left: 0,
              bottom: 0
            }} data-id="element-370">
                <CartesianGrid strokeDasharray="3 3" data-id="element-371" />
                <XAxis dataKey="name" data-id="element-372" />
                <YAxis data-id="element-373" />
                <Tooltip data-id="element-374" />
                <Area type="monotone" dataKey="p2p" stackId="1" stroke="#F58220" fill="#F58220" fillOpacity={0.6} name="P2P" data-id="element-375" />
                <Area type="monotone" dataKey="qrc" stackId="1" stroke="#1A1F36" fill="#1A1F36" fillOpacity={0.6} name="QRC" data-id="element-376" />
                <Area type="monotone" dataKey="rtp" stackId="1" stroke="#4C9AFF" fill="#4C9AFF" fillOpacity={0.6} name="RTP" data-id="element-377" />
                <Area type="monotone" dataKey="bulk" stackId="1" stroke="#36B37E" fill="#36B37E" fillOpacity={0.6} name="Bulk" data-id="element-378" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-4" data-id="element-379">
          <div className="flex justify-between items-center mb-4" data-id="element-380">
            <h3 className="font-medium text-[#1A1F36]" data-id="element-381">Transaction Value</h3>
            <div className="flex items-center text-xs text-gray-500" data-id="element-382">
              <InfoIcon size={14} className="mr-1" data-id="element-383" />
              <span data-id="element-384">
                {timeFrame === 'today' && 'Today'}
                {timeFrame === 'month' && 'This Month'}
                {timeFrame === 'year' && 'This Year'}
              </span>
            </div>
          </div>
          <div className="h-64" data-id="element-385">
            <ResponsiveContainer width="100%" height="100%" data-id="element-386">
              <BarChart data={getValueData()} margin={{
              top: 10,
              right: 30,
              left: 0,
              bottom: 0
            }} data-id="element-387">
                <CartesianGrid strokeDasharray="3 3" data-id="element-388" />
                <XAxis dataKey="name" data-id="element-389" />
                <YAxis data-id="element-390" />
                <Tooltip formatter={value => [`ETB ${value.toLocaleString()}`, '']} data-id="element-391" />
                <Legend data-id="element-392" />
                <Bar dataKey="p2p" name="P2P" fill="#F58220" data-id="element-393" />
                <Bar dataKey="qrc" name="QRC" fill="#1A1F36" data-id="element-394" />
                <Bar dataKey="rtp" name="RTP" fill="#4C9AFF" data-id="element-395" />
                <Bar dataKey="bulk" name="Bulk" fill="#36B37E" data-id="element-396" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
      <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden" data-id="element-397">
        <div className="p-4 border-b border-gray-200" data-id="element-398">
          <h3 className="font-medium text-[#1A1F36]" data-id="element-399">Recent Activity</h3>
        </div>
        <div className="overflow-x-auto" data-id="element-400">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-401">
            <thead className="bg-gray-50" data-id="element-402">
              <tr data-id="element-403">
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-404">
                  Date & Time
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-405">
                  Sender Bank
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-406">
                  Receiver Bank
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-407">
                  Amount
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-408">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-409">
              {recentActivity.map(activity => <tr key={activity.id} className={activity.status === 'Failed' ? 'bg-red-50' : ''} data-id="element-410">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-411">
                    {activity.dateTime}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-412">
                    {activity.sender}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-413">
                    {activity.receiver}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-414">
                    ETB {activity.amount.toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-415">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${activity.status === 'Success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`} data-id="element-416">
                      {activity.status}
                    </span>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </div>
    </div>;
}